type AnimationType = "breathe" | "wave";

interface MascotFallbackProps {
  animationType?: AnimationType;
  size?: number;
}

function BreatheIllustration() {
  return (
    <svg
      viewBox="0 0 140 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="h-full w-full"
    >
      <defs>
        <radialGradient id="mf-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="mf-body" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0d1e2e" />
          <stop offset="100%" stopColor="#081018" />
        </linearGradient>
        <linearGradient id="mf-visor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="mf-tablet" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0d2b2b" />
          <stop offset="100%" stopColor="#071515" />
        </linearGradient>
        <filter id="mf-blur">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        <clipPath id="mf-head-clip">
          <rect x="32" y="10" width="76" height="64" rx="22" />
        </clipPath>
      </defs>

      {/* Ambient glow */}
      <ellipse cx="70" cy="90" rx="52" ry="48" fill="url(#mf-glow)" filter="url(#mf-blur)" />

      {/* Head */}
      <rect x="32" y="10" width="76" height="64" rx="22" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.4" />

      {/* Visor */}
      <rect x="44" y="22" width="52" height="32" rx="8" fill="url(#mf-visor)" opacity="0.8" />

      {/* Visor scanlines */}
      <rect x="44" y="22" width="52" height="32" rx="8" clipPath="url(#mf-head-clip)" fill="none"
        stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.08"
        strokeDasharray="2 4" strokeDashoffset="0" />

      {/* Eye dots */}
      <circle cx="57" cy="38" r="5" fill="#2dd4bf" opacity="0.9" />
      <circle cx="83" cy="38" r="5" fill="#2dd4bf" opacity="0.9" />
      <circle cx="57" cy="38" r="2.5" fill="#ffffff" opacity="0.7" />
      <circle cx="83" cy="38" r="2.5" fill="#ffffff" opacity="0.7" />

      {/* Mouth indicator */}
      <path d="M57 50 Q70 57 83 50" stroke="#2dd4bf" strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" fill="none" />

      {/* Neck */}
      <rect x="59" y="74" width="22" height="10" rx="4" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="0.8" strokeOpacity="0.25" />

      {/* Body */}
      <rect x="24" y="84" width="92" height="68" rx="18" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.3" />

      {/* Body accent line */}
      <line x1="40" y1="110" x2="100" y2="110" stroke="#2dd4bf" strokeWidth="0.7" strokeOpacity="0.2" />

      {/* Chest indicator */}
      <rect x="54" y="94" width="32" height="18" rx="6" fill="#2dd4bf" fillOpacity="0.08" stroke="#2dd4bf" strokeWidth="0.8" strokeOpacity="0.3" />
      <circle cx="70" cy="103" r="4" fill="#2dd4bf" opacity="0.4" />

      {/* Left arm */}
      <rect x="6" y="88" width="20" height="46" rx="10" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.25" />

      {/* Right arm — holding tablet */}
      <rect x="114" y="88" width="20" height="46" rx="10" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.25" />

      {/* Tablet held by right arm */}
      <rect x="106" y="100" width="30" height="44" rx="5" fill="url(#mf-tablet)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.5" />
      {/* Tablet screen glow */}
      <rect x="109" y="104" width="24" height="32" rx="3" fill="#2dd4bf" fillOpacity="0.12" />
      <rect x="109" y="104" width="24" height="32" rx="3" stroke="#2dd4bf" strokeWidth="0.5" strokeOpacity="0.4" />
      {/* Tablet screen content lines */}
      <line x1="112" y1="110" x2="130" y2="110" stroke="#2dd4bf" strokeOpacity="0.5" strokeWidth="1" />
      <line x1="112" y1="116" x2="127" y2="116" stroke="#2dd4bf" strokeOpacity="0.3" strokeWidth="1" />
      <line x1="112" y1="122" x2="130" y2="122" stroke="#2dd4bf" strokeOpacity="0.5" strokeWidth="1" />
      <line x1="112" y1="128" x2="125" y2="128" stroke="#2dd4bf" strokeOpacity="0.3" strokeWidth="1" />

      {/* Legs */}
      <rect x="38" y="150" width="24" height="22" rx="10" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.2" />
      <rect x="78" y="150" width="24" height="22" rx="10" fill="url(#mf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.2" />

      {/* Foot glow */}
      <ellipse cx="50" cy="172" rx="14" ry="5" fill="#2dd4bf" fillOpacity="0.08" />
      <ellipse cx="90" cy="172" rx="14" ry="5" fill="#2dd4bf" fillOpacity="0.08" />
    </svg>
  );
}

function WaveIllustration() {
  return (
    <svg
      viewBox="0 0 140 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className="h-full w-full"
    >
      <defs>
        <radialGradient id="wf-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="wf-body" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0d1e2e" />
          <stop offset="100%" stopColor="#081018" />
        </linearGradient>
        <linearGradient id="wf-visor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.6" />
        </linearGradient>
        <filter id="wf-blur">
          <feGaussianBlur stdDeviation="8" />
        </filter>
      </defs>

      {/* Ambient glow */}
      <ellipse cx="70" cy="90" rx="55" ry="50" fill="url(#wf-glow)" filter="url(#wf-blur)" />

      {/* Head */}
      <rect x="32" y="10" width="76" height="64" rx="22" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.4" />

      {/* Visor */}
      <rect x="44" y="22" width="52" height="32" rx="8" fill="url(#wf-visor)" opacity="0.8" />

      {/* Eyes — happy/waving */}
      <path d="M50 38 Q57 32 64 38" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.85" />
      <path d="M76 38 Q83 32 90 38" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.85" />

      {/* Happy mouth */}
      <path d="M55 52 Q70 62 85 52" stroke="#2dd4bf" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.8" />

      {/* Neck */}
      <rect x="59" y="74" width="22" height="10" rx="4" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="0.8" strokeOpacity="0.25" />

      {/* Body */}
      <rect x="28" y="84" width="84" height="68" rx="18" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.3" />

      {/* Body accent */}
      <rect x="50" y="96" width="40" height="18" rx="6" fill="#2dd4bf" fillOpacity="0.07" stroke="#2dd4bf" strokeWidth="0.8" strokeOpacity="0.25" />
      <circle cx="70" cy="105" r="4" fill="#2dd4bf" opacity="0.35" />

      {/* Left arm — down */}
      <rect x="10" y="90" width="20" height="42" rx="10" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.25" />

      {/* Right arm — raised/waving (rotated) */}
      <rect
        x="110" y="56" width="20" height="46" rx="10"
        fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.4"
        transform="rotate(-45, 120, 79)"
      />

      {/* Wave hand */}
      <ellipse cx="126" cy="52" rx="10" ry="8"
        fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1.2" strokeOpacity="0.5"
        transform="rotate(-20, 126, 52)"
      />
      {/* Finger hints */}
      <line x1="118" y1="47" x2="114" y2="38" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.5" />
      <line x1="123" y1="44" x2="121" y2="34" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.5" />
      <line x1="128" y1="44" x2="128" y2="34" stroke="#2dd4bf" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.5" />

      {/* Motion lines near waving hand */}
      <line x1="108" y1="36" x2="101" y2="36" stroke="#2dd4bf" strokeOpacity="0.3" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="110" y1="30" x2="104" y2="27" stroke="#2dd4bf" strokeOpacity="0.2" strokeWidth="1" strokeLinecap="round" />

      {/* Legs */}
      <rect x="40" y="150" width="22" height="22" rx="10" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.2" />
      <rect x="78" y="150" width="22" height="22" rx="10" fill="url(#wf-body)" stroke="#2dd4bf" strokeWidth="1" strokeOpacity="0.2" />

      {/* Ground glow */}
      <ellipse cx="70" cy="175" rx="38" ry="6" fill="#2dd4bf" fillOpacity="0.06" />
    </svg>
  );
}

export function MascotFallback({
  animationType = "breathe",
  size = 380,
}: MascotFallbackProps) {
  return (
    <div
      style={{ width: size, height: size }}
      className="relative flex items-center justify-center"
      aria-hidden
    >
      <div className="pointer-events-none absolute inset-0 rounded-full bg-teal-500/10 blur-3xl" />
      <div style={{ width: size * 0.7, height: size * 0.88 }} className="relative">
        {animationType === "breathe" ? (
          <BreatheIllustration />
        ) : (
          <WaveIllustration />
        )}
      </div>
    </div>
  );
}
